import { Grid } from "@material-ui/core";
import { isNull, omitBy } from "lodash";
import styled from "styled-components";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { PrimaryButton } from "../../../components/button";
import { Form } from "../../../components/forms";
import { Country } from "../../../components/forms/country";
import { EditableField } from "../../../components/forms/field";
import { PhoneNumber } from "../../../components/forms/phone-number";
import { Avatar } from "../../../components/header/avatar";
import { Flex } from "../../../components/layout";
import { H2 } from "../../../components/typography";
import { Widget } from "../../../components/widget";
import { UserActions } from "../../../store/users/actions";
import { UsersDTO } from "../../../store/users/models/dto";
import {
  useYupValidationResolver,
  NullablePartial,
} from "../../../utils/utils";
import {
  ButtonWrapper,
  Fields,
  Section,
  StyledDateField,
  StyledTextField,
} from "../base";
import { Buttons } from "./buttons";

type RequestDTO = UsersDTO.Requests.UserDetails.T;
const { useFields, defaults } = UsersDTO.Requests.UserDetails;

export const EditButtons = styled(Flex)`
  justify-content: flex-end;
`;

export const GeneralInfo: React.FunctionComponent<{
  userDetails: NullablePartial<RequestDTO>;
}> = ({ userDetails }) => {
  const { labels, validation } = useFields();
  const dispatch = useDispatch();
  const [editing, setEditing] = useState(false);
  const resolver = useYupValidationResolver(validation);
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
    setValue,
    watch,
  } = useForm<RequestDTO>({
    defaultValues: { ...defaults, ...omitBy(userDetails, isNull) },
    resolver,
  });

  const country = watch("country");

  useEffect(() => {
    const phoneValue = getValues("phone");
    if (phoneValue === undefined || phoneValue === "") {
      setValue("phoneCountryCode", country);
    }
  }, [country, getValues, setValue]);

  useEffect(() => {
    if (!editing) reset();
  }, [editing, reset]);

  return (
    <Form
      onSubmit={handleSubmit((x) =>
        dispatch(
          UserActions.postUserDetails({
            ...omitBy(userDetails, isNull),
            ...(x as RequestDTO),
          }) //TODO atm counting on form validating
        )
      )}
    >
      <Widget
        title="Personal Information"
        adornment={<Buttons setEditing={setEditing} editing={editing} />}
      >
        <Fields>
          <div>
            <EditableField editing={editing} control={control} name="firstName">
              <StyledTextField
                fullWidth
                type="text"
                label={labels.firstName}
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
              />
            </EditableField>
            <EditableField
              editing={editing}
              control={control}
              name="middleName"
            >
              <StyledTextField
                fullWidth
                type="text"
                label={labels.middleName}
                error={!!errors.middleName}
                helperText={errors.middleName?.message}
              />
            </EditableField>
            <EditableField editing={editing} control={control} name="lastName">
              <StyledTextField
                fullWidth
                type="text"
                label={labels.lastName}
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
              />
            </EditableField>
          </div>
          <div>
            <Avatar scale={2} />
          </div>
        </Fields>
        <Section>
          <Grid item xs={12}>
            <EditableField
              editing={false}
              control={control}
              name="emailAddress"
            >
              <StyledTextField
                fullWidth
                type="email"
                label={labels.emailAddress}
                error={!!errors.emailAddress}
                helperText={errors.emailAddress?.message}
              />
            </EditableField>
          </Grid>
          <Grid item xs={12} sm={8}>
            <EditableField
              editing={editing}
              control={control}
              name="nationality"
            >
              <Country
                fullWidth
                label={labels.nationality}
                error={!!errors.nationality}
                helperText={errors.nationality?.message}
              />
            </EditableField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <EditableField
              editing={editing}
              control={control}
              name="dateOfBirth"
            >
              <StyledDateField
                fullWidth
                label={labels.dateOfBirth}
                error={!!errors.dateOfBirth}
                helperText={errors.dateOfBirth?.message}
              />
            </EditableField>
          </Grid>
        </Section>
        <Section>
          <Grid item xs={12}>
            <H2>Main Residential Address</H2>
          </Grid>
          <Grid item xs={12}>
            <EditableField
              editing={editing}
              control={control}
              name="streetAddress"
            >
              <StyledTextField
                fullWidth
                type="text"
                label={labels.streetAddress}
                error={!!errors.streetAddress}
                helperText={errors.streetAddress?.message}
              />
            </EditableField>
          </Grid>
          <Grid item sm={6} xs={12}>
            <EditableField editing={editing} control={control} name="city">
              <StyledTextField
                fullWidth
                type="text"
                label={labels.city}
                error={!!errors.city}
                helperText={errors.city?.message}
              />
            </EditableField>
          </Grid>
          <Grid item xs={8} sm={6}>
            <EditableField editing={editing} control={control} name="state">
              <StyledTextField
                fullWidth
                type="text"
                label={labels.state}
                error={!!errors.state}
                helperText={errors.state?.message}
              />
            </EditableField>
          </Grid>
          <Grid item xs={4}>
            <EditableField editing={editing} control={control} name="zip">
              <StyledTextField
                fullWidth
                type="text"
                label={labels.zip}
                error={!!errors.zip}
                helperText={errors.zip?.message}
              />
            </EditableField>
          </Grid>
          <Grid item xs={12} sm={8}>
            <EditableField editing={editing} control={control} name="country">
              <Country
                fullWidth
                label={labels.country}
                error={!!errors.country}
                helperText={errors.country?.message}
              />
            </EditableField>
          </Grid>
          <Grid item sm={6} xs={12}>
            <PhoneNumber
              editing={editing}
              label={labels.phone}
              error={errors.phoneCountryCode?.message || errors.phone?.message}
              value={{
                phone: getValues().phone,
                prefix: getValues().phoneCountryCode,
              }}
              onChange={({ prefix, phone }) => {
                setValue("phone", phone || "");
                setValue("phoneCountryCode", prefix || "");
              }}
            />
          </Grid>
        </Section>
        {editing ? (
          <EditButtons>
            <ButtonWrapper>
              <PrimaryButton
                variant="outlined"
                onClick={() => setEditing(false)}
              >
                Cancel
              </PrimaryButton>
            </ButtonWrapper>
            <ButtonWrapper>
              <PrimaryButton variant="contained" type="submit">
                Save
              </PrimaryButton>
            </ButtonWrapper>
          </EditButtons>
        ) : null}
      </Widget>
    </Form>
  );
};
